import { createContext, useEffect, useRef, useState } from 'react'

// Matches Play's `useWebSocket` in `play/playfrontend/src/api/hooks.ts`: a
// bounded number of retries with an exponential backoff. Without the bound, a
// consumer that keeps refusing the handshake (HTTP 403 when the session has
// expired) is retried for as long as the tab stays open.
const INITIAL_RECONNECT_DELAY_MS = 1000
const MAX_RECONNECT_DELAY_MS = 30000
const MAX_RECONNECT_ATTEMPTS = 5

// Sent by `NotificationsConsumer` (see `api/notifications/consumers.py`) when
// the socket will never be authorised: the session is gone, or the account has
// no usable GUID to build a notification group from. Reconnecting can only earn
// the same refusal, so the socket is abandoned until something that could change
// the answer happens — a fresh sign-in, or a page load.
export const WS_CLOSE_UNAUTHORISED = 4403

export function buildWebSocketUrl(baseApiUrl: string): string {
	const protocol = baseApiUrl.includes('localhost') ? 'ws' : 'wss'
	const baseUrl = baseApiUrl.replace('http://', '').replace('https://', '')
	return `${protocol}://${baseUrl}learn/ws/notifications/`
}

export interface WebSocketContextValue {
	socket: WebSocket | null
	connected: boolean
}

export const WebSocketContext = createContext<WebSocketContextValue>({
	socket: null,
	connected: false,
})

// Opens a single WebSocket connection while `enabled` (i.e. once the user is
// known to be authenticated) and reconnects on disconnect, mirroring Play's
// `play/playfrontend/src/api/hooks.ts`.
export function useWebSocketConnection(enabled: boolean): WebSocketContextValue {
	const [socket, setSocket] = useState<WebSocket | null>(null)
	const [connected, setConnected] = useState(false)
	const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

	useEffect(() => {
		if (!enabled) {
			setSocket(null)
			setConnected(false)
			return undefined
		}

		let isUnmounted = false
		let currentSocket: WebSocket | null = null
		let retryCount = 0
		let reconnectDelay = INITIAL_RECONNECT_DELAY_MS
		// A socket that has opened at least once proves the server will accept
		// this session, so a later drop is worth chasing. One that has never
		// opened is being refused, and repeating the request won't change that.
		let hasEverOpened = false
		let isUnauthorised = false

		const connect = () => {
			const ws = new WebSocket(
				buildWebSocketUrl(import.meta.env.VITE_BASE_API_URL!),
			)
			currentSocket = ws

			ws.onopen = () => {
				hasEverOpened = true
				retryCount = 0
				reconnectDelay = INITIAL_RECONNECT_DELAY_MS
				setConnected(true)
			}

			ws.onclose = (event: CloseEvent) => {
				currentSocket = null
				setConnected(false)
				if (event.code === WS_CLOSE_UNAUTHORISED) {
					isUnauthorised = true
					return
				}
				if (isUnmounted || retryCount >= MAX_RECONNECT_ATTEMPTS) {
					return
				}
				retryCount += 1
				reconnectTimeoutRef.current = setTimeout(connect, reconnectDelay)
				reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS)
			}

			ws.onerror = () => {
				ws.close()
			}

			setSocket(ws)
		}

		// A tab returning to the foreground is a cheap signal that it's worth
		// starting over: a connection that used to work may be reachable again.
		// It says nothing about a socket the server has been refusing all along,
		// so those keep their spent budget — otherwise every tab switch bought
		// five more rejected handshakes, and the attempt cap never bit.
		const retryOnVisible = () => {
			if (document.visibilityState !== 'visible' || currentSocket) {
				return
			}
			if (isUnauthorised || !hasEverOpened) {
				return
			}
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current)
			}
			retryCount = 0
			reconnectDelay = INITIAL_RECONNECT_DELAY_MS
			connect()
		}
		document.addEventListener('visibilitychange', retryOnVisible)

		connect()

		return () => {
			isUnmounted = true
			document.removeEventListener('visibilitychange', retryOnVisible)
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current)
			}
			currentSocket?.close()
		}
	}, [enabled])

	return { socket, connected }
}
